import { useState } from 'react';
import { Check, ChevronsUpDown, Plus } from 'lucide-react';
import { Dropdown } from '@/components/ui/Dropdown';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/useToast';
import { ApiRequestError } from '@/services/httpClient';
import { resolveFileUrl } from '@/utils/fileUrl';

function orgInitials(name: string): string {
  return name.trim().slice(0, 2).toUpperCase();
}

interface WorkspaceSwitcherProps {
  collapsed?: boolean;
  /** 'sidebar' (default) — full-width row for the nav column. 'navbar' — compact bordered pill for the topbar. */
  variant?: 'sidebar' | 'navbar';
}

export function WorkspaceSwitcher({ collapsed = false, variant = 'sidebar' }: WorkspaceSwitcherProps) {
  const { organizations, organizationId, switchOrganization } = useAuth();
  const { show } = useToast();
  const [switchingId, setSwitchingId] = useState<number | null>(null);

  const current = organizations.find((org) => org.id === organizationId) ?? organizations[0];

  async function handleSelect(id: number, close: () => void) {
    if (id === organizationId) {
      close();
      return;
    }
    setSwitchingId(id);
    try {
      await switchOrganization(id);
      show('Switched workspace.', 'success');
    } catch (err) {
      show(err instanceof ApiRequestError ? err.message : 'Could not switch workspace.');
    } finally {
      setSwitchingId(null);
      close();
    }
  }

  if (!current) {
    return null;
  }

  const isNavbar = variant === 'navbar';

  return (
    <Dropdown
      align="left"
      width={260}
      trigger={({ onClick, isOpen }) => (
        <button
          type="button"
          onClick={onClick}
          className={
            isNavbar
              ? `flex items-center gap-2 rounded-xl border border-gray-200 py-1.5 pl-1.5 pr-2.5 text-left transition-colors hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800 ${
                  isOpen ? 'bg-gray-50 dark:bg-gray-800' : ''
                }`
              : `flex w-full items-center gap-2.5 rounded-xl px-2.5 py-2 text-left transition-colors hover:bg-gray-100 dark:hover:bg-gray-800 ${
                  isOpen ? 'bg-gray-100 dark:bg-gray-800' : ''
                }`
          }
        >
          <div
            className={`flex shrink-0 items-center justify-center rounded-lg bg-brand-600 font-bold text-white ${
              isNavbar ? 'h-7 w-7 text-[11px]' : 'h-8 w-8 text-xs'
            }`}
          >
            {current.logo_url ? (
              <img src={resolveFileUrl(current.logo_url) ?? undefined} alt="" className="h-full w-full rounded-lg object-cover" />
            ) : (
              orgInitials(current.name)
            )}
          </div>
          {isNavbar && (
            <>
              <span className="max-w-[160px] truncate text-sm font-semibold text-gray-900 dark:text-gray-100 sm:max-w-[240px]">
                {current.name}
              </span>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            </>
          )}
          {!isNavbar && !collapsed && (
            <>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{current.name}</p>
                <p className="truncate text-xs text-gray-400">Workspace</p>
              </div>
              <ChevronsUpDown className="h-3.5 w-3.5 shrink-0 text-gray-400" />
            </>
          )}
        </button>
      )}
    >
      {(close) => (
        <div className="flex flex-col gap-0.5">
          <p className="px-2.5 pb-1 pt-1 text-xs font-semibold uppercase tracking-wide text-gray-400">
            Your organizations
          </p>
          {organizations.map((org) => (
            <button
              key={org.id}
              type="button"
              disabled={switchingId !== null}
              onClick={() => void handleSelect(org.id, close)}
              className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm hover:bg-gray-100 disabled:opacity-60 dark:hover:bg-gray-800"
            >
              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gray-100 text-[10px] font-bold text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {orgInitials(org.name)}
              </div>
              <span className="min-w-0 flex-1 truncate font-medium text-gray-700 dark:text-gray-300">{org.name}</span>
              {org.id === organizationId && <Check className="h-4 w-4 shrink-0 text-brand-600 dark:text-brand-400" />}
            </button>
          ))}
          <div className="my-1 border-t border-gray-100 dark:border-gray-800" />
          <button
            type="button"
            onClick={() => {
              show('Creating additional organizations is coming soon.');
              close();
            }}
            className="flex items-center gap-2.5 rounded-xl px-2.5 py-2 text-left text-sm font-medium text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <Plus className="h-4 w-4" />
            New organization
          </button>
        </div>
      )}
    </Dropdown>
  );
}
